<script lang="ts" setup>
    import '../../../../css/choice.css';
    import {onMounted,watch,reactive} from 'vue';   
    import {IClaim,ClaimDisplay,ClaimSimpleLists,paymentTypeWarning} from '../../../lib/Models/ClaimModel'; 
    import ClaimList from '../Subcomponents/ClaimList.vue';
    import TextInput from '../../../components/TextInput.vue';
    import ActionButtonV2 from '../../../components/ActionButtonV2.vue'; 
    import SimpleList from '../../../components/SimpleList.vue';
    import {ActionButtonStyle} from '../../../lib/Types/Types'; 
    import {ButtonDisplaySize,ButtonDisplayStyle} from '../../../Enums/UiEnums';
    import {prettyCurrency} from '../../../lib/Formatting/Number';   
    import {initPinia} from '../../../lib/PiniaMgr';
    import {useRefundFullfillmentStore} from '../../../lib/Stores/RefundFullfillmentStore';
 
    const emit = defineEmits(["saveRefunds"]);
    const refundFullfillmentStore = useRefundFullfillmentStore(initPinia());
    
    const formData = reactive <
    {   
        claimListing?: IClaim[],
        showRefunds: boolean,
        disableRefunds: boolean,
        notes?: string,
        window: {headerHeight: number, footerHeight: number, windowPadding: number, workareaHeight: number},
    }>({      
        showRefunds: false,
        disableRefunds: true,
        notes: "",
        window: {headerHeight: 7.5, footerHeight: 6, windowPadding: 2.5, workareaHeight: 8}, 
    });
    const props = withDefaults(defineProps<{ 
        saveEnabled?: number,
        claimList?: IClaim[],
        autodistribute?: boolean
    }>(),
    {
        saveEnabled: 0,
        autodistribute: false
    }); 
    
    onMounted(async () => {  
        resetHeights();
        await refundFullfillmentStore.getRefundSettings();
         
        if (!props.claimList) {
            return;
        }
        formData.claimListing = props.claimList;
    });
    watch(() => props.claimList, (newclaimList : IClaim[] | undefined, oldclaimList : IClaim[] | undefined) => { 
        if (!newclaimList) { 
            formData.showRefunds = false;
            refundFullfillmentStore.clearRefunds();
            return;
        }
        if (JSON.stringify(newclaimList) != JSON.stringify(oldclaimList) ) {  
            refundFullfillmentStore.clearRefunds();
            formData.claimListing = newclaimList;
            formData.showRefunds = true;
        }
    });
    async function saveRefunds() : Promise<void> {
        const refunds_saved = await refundFullfillmentStore.saveRefunds(formData.notes);
        if (refunds_saved) {
            emit("saveRefunds");
        }
    }
    function resetHeights(): void { 
        const windowHeight = (window.innerHeight / 16) * .85;
        formData.window.workareaHeight = windowHeight - formData.window.headerHeight - formData.window.footerHeight - formData.window.windowPadding;
    }
    function onChangePaymentType(index: number, selectedItem : any) : void { 
        refundFullfillmentStore.refund_headers[index].paymentType = selectedItem.value;      
    }
</script> 
 
<style scoped> 
.billing-header {
  color: #204D89;
  background-color: #f1f1f1;
}
.billing-data-left {
  color: black;
  background-color: white;
  text-align: left;
}    
.billing-data-center {
  color: black;
  background-color: white;
  text-align: center;
}
.billing-data-right {
  color: black;
  background-color: white;
  text-align: right;
}
.billing-cell {
  border: 1px solid #6A8CCB;
  FONT-SIZE: 8pt;
  font-family: Arial, sans-serif, Helvetica;
  padding: 5px 5px 5px 5px;
  cursor: default;
  vertical-align: middle;
}
</style>
<template>
    <div class="row skinnyscroll" :style="{ 'overflow-y': 'scroll', 'overflow-x': 'hidden',  'height': (formData.window.headerHeight + 'rem')}">
        <div class="col" >
            <table border="1" aria-hidden="true" style="table-layout: fixed; border-collapse: collapse; width: 47.5rem;">
                <colgroup>
                    <col style="width: 19.4rem">
                    <col style="width: 4.7rem">
                    <col style="width: 4.7rem"> 
                    <col style="width: 18.7rem">
                </colgroup>
                <tr>
                    <td class="billing-header billing-cell text-center">Refundee</td>
                    <td class="billing-header billing-cell text-center">Max. Refund</td>
                    <td class="billing-header billing-cell text-center">Tot. Refund</td> 
                    <td class="billing-header billing-cell text-center">Payment Type</td>
                </tr>
                <tr class="col" v-for="(headerRecord,index) in refundFullfillmentStore.refund_headers" :key="`RF${headerRecord.refundEID}`">
                    <td class="billing-cell" :title="`Name: ${headerRecord.payerName}\r\nType: ${headerRecord.payerType}`">{{ headerRecord.payerName }}</td>
                    <td class="billing-cell billing-data-right">{{ prettyCurrency(headerRecord.maxRefund) }}</td>
                    <td :class="`billing-cell billing-data-right ${(headerRecord.selectedRefund > headerRecord.maxRefund ? 'text-danger': '' )}`">{{ prettyCurrency(headerRecord.selectedRefund) }}</td>
                   
                    <td class="billing-cell">
                        <SimpleList 
                            :listName="ClaimSimpleLists.RefundPaymentType"
                            :listWarning="paymentTypeWarning"
                            :includeHeader="true"
                            :includeBlank="false" 
                            v-model:selected="headerRecord.paymentType"
                            @onChange="val => onChangePaymentType(index,val)" 
                        />
                    </td>
                </tr>
            </table>
        </div> 
    </div> 
    <div class="row skinnyscroll pt-3" :style="{'overflow-y': 'scroll', 'overflow-x': 'hidden', 'height': (formData.window.workareaHeight + 'rem')}">
        <div class="col">
            <ClaimList v-if="formData.showRefunds"
                :saveEnabled="props.saveEnabled"
                :selection="false"
                :autodistribute="props.autodistribute"
                :claimList = "formData.claimListing"
                :display="ClaimDisplay.Refund" 
                :displayProcedures="false"
                :displayPayments="false"
                :displayComments="false"
            />
        </div>
    </div>
    <div :style="{'overflow': 'hidden', 'height': (formData.window.footerHeight + 'rem')}">  
        <div class="row pt-2">
            <div class="col">
                <TextInput 
                    :rows=2
                    :cssFramework="'vanilla'"  
                    v-model:textValue="formData.notes" 
                    :placeholder="'Refund Notes'" 
                />
            </div>
        </div>
        <div class="row pt-2"> 
            <div class="col-10">
                <span v-show="refundFullfillmentStore.refund_error" class="fst-italic label-isalus-small text-danger">Amount entered exceeds credited amount.</span>
            </div>
            <div class="col-2 text-end">
                <ActionButtonV2 
                    :disabled="refundFullfillmentStore.refund_save_disabled"
                    :action="ActionButtonStyle.Save"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :spinText="'Refunding'"
                    :performAction="saveRefunds"
                />
            </div>
        </div>
    </div>
</template>
